/**
 * Portal entitlements for the Fleet Maintenance and Audit Center tabs.
 *
 * Answers whether the carrier being shown can see Fleet Maintenance
 * (`/repairs`, `/invoice-review`) and Audit Center (`/audits`). The fleet rows shipped into the SHARED portal nav on
 * 2026-08-26/27 with no gate at all and turned up in the sales demo; this is
 * the gate. The router hides the nav rows and guards the routes off the same
 * flag, so a hidden row can never be reached by URL and a guarded route can
 * never dangle in the sidebar (Dugger's 08-25 "why did Challenge stop
 * working" shape).
 *
 * Source of truth: `GET /api/v1/client/features` →
 * `{"features": {"fleet-maintenance": bool, ...}}`. For a CLIENT that is
 * their own client's flags; for STAFF in view-as the shared axios request
 * interceptor (`packages/shared/src/services/api.ts`) appends
 * `?client_id=<viewAsClientId>` to every `/v1/client/` request, so the same
 * call answers for both personas.
 *
 * Portal-local on purpose, like `seatReview.ts`: the gate is a portal-only
 * concern and keeping it out of packages/shared keeps its blast radius here.
 *
 * FAILS CLOSED — the deliberate opposite of `seatReview.ts`, which fails
 * OPEN. Seat review is a lock: a 500 there must never keep a carrier away
 * from their compliance data. This is an unlock for a feature no client is
 * on today, so hiding it on any error (including the 404 the route returns
 * until it is deployed) cannot regress anyone, while showing it on error is
 * exactly the incident this exists to close.
 */
import { defineStore } from 'pinia'
import { ref, watch } from 'vue'
import { api, useAuthStore } from '@claimshield/shared'

/** The route's body. `features` is the documented shape; `data.features` is
 * tolerated so a later move onto the portal's usual `{status, data}` envelope
 * cannot silently turn every carrier's flags into "off". */
interface ClientFeaturesResponse {
  features?: Record<string, boolean> | null
  data?: { features?: Record<string, boolean> | null } | null
}

const FLEET_MAINTENANCE_FLAG = 'fleet-maintenance'
const AUDIT_CENTER_FLAG = 'audit-center'

export const useFleetEntitlementStore = defineStore('fleetEntitlement', () => {
  const auth = useAuthStore()

  const loaded = ref(false)
  const fleetMaintenance = ref(false)
  const auditCenter = ref(false)

  // Identity the cached answer is valid for. Keyed on the CLIENT being shown,
  // not the logged-in user: a staff member switching view-as changes the
  // carrier underneath the layout without any navigation (PortalLayout
  // re-keys the child RouterView, nothing else fires), and a client logging
  // out and back in as a different client inside the SPA never remounts the
  // store. Same reasoning as `seatReview.ts`'s `loadedForUserId`.
  let loadedForKey: string | null = null

  // In-flight memoization keyed the same way — two navigations can race the
  // router guard before `loaded` flips, and a promise started for one
  // identity must never be handed to a caller asking about another.
  let inFlight: Promise<void> | null = null
  let inFlightForKey: string | null = null

  // The route 404s until its Sentinel side is deployed. One debug line, not
  // one per navigation.
  let failureLogged = false

  function identityKey(): string | null {
    return auth.viewAsClientId ?? auth.user?.id ?? null
  }

  /** The single write path. Applies only if the identity the fetch was
   * started for is still the identity on screen — a response that lands
   * after a view-as switch belongs to the previous carrier and is dropped
   * (its own refetch is already in flight, see the watcher below). */
  function apply(fleetEnabled: boolean, auditEnabled: boolean, requestedForKey: string | null): void {
    if (identityKey() !== requestedForKey) return
    fleetMaintenance.value = fleetEnabled
    auditCenter.value = auditEnabled
    loaded.value = true
    loadedForKey = requestedForKey
  }

  async function refresh(requestedForKey: string | null = identityKey()): Promise<void> {
    try {
      const res = await api.get<ClientFeaturesResponse>('/v1/client/features')
      const features = res.data?.features ?? res.data?.data?.features ?? null
      apply(features?.[FLEET_MAINTENANCE_FLAG] === true,
        features?.[AUDIT_CENTER_FLAG] === true, requestedForKey)
    } catch (err) {
      if (!failureLogged) {
        failureLogged = true
        console.debug(
          '[fleetEntitlement] GET /v1/client/features failed; treating fleet-maintenance as off',
          err,
        )
      }
      // Closed. Never rethrow — this runs inside router guards.
      apply(false, false, requestedForKey)
    }
  }

  async function ensureLoaded(): Promise<void> {
    // No session (logout clears `user` and `viewAsClientId` in turn, and the
    // watcher below fires on the second): nothing to ask, answer closed.
    if (!auth.isAuthenticated) {
      fleetMaintenance.value = false
      auditCenter.value = false
      loaded.value = false
      loadedForKey = null
      return
    }
    const key = identityKey()
    if (inFlight && inFlightForKey === key) return inFlight
    if (loaded.value && loadedForKey === key) return

    // A different carrier than the one we answered for: drop the old answer
    // NOW rather than showing the previous client's rows for a round trip.
    // Closed is the resting state.
    if (loadedForKey !== key) {
      fleetMaintenance.value = false
      auditCenter.value = false
      loaded.value = false
    }

    inFlightForKey = key
    const promise = refresh(key).finally(() => {
      // Only clear bookkeeping still tracking THIS fetch — a fast identity
      // switch may already have started a newer one this must not stomp on.
      if (inFlightForKey === key) {
        inFlight = null
        inFlightForKey = null
      }
    })
    inFlight = promise
    return promise
  }

  // Staff view-as switch: no navigation fires, so the router's beforeEach
  // never runs — refetch here so the sidebar follows the carrier on screen.
  watch(
    () => auth.viewAsClientId,
    () => {
      void ensureLoaded()
    },
  )

  return { loaded, fleetMaintenance, auditCenter, ensureLoaded, refresh }
})
